import React, { useEffect, useCallback, useState } from 'react'
import BlogGrid from '../../components/Blog/BlogGrid';
import BlogHeader from '../../components/Blog/BlogHeader';
import { useSelector } from 'react-redux';
import { selectUserInformations } from '../../features/authSlice';
import BlogSkeleton from '../../components/Blog/BlogSkeleton';
import BlogPostModal from '../../components/Blog/BlogPostModal';
import CreatePostModal from '../../components/Blog/CreatePostModal';
import { useBlog } from '../../hooks/useBlog';
import { useSearchParams } from 'react-router-dom';

const DEBOUNCE_DELAY = 600;

const BlogPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeCategoryId, setActiveCategoryId] = useState(null);
    const selectedPostId = searchParams.get('postId');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const user = useSelector(selectUserInformations);
    const {
        posts,
        categories,
        loading,
        categoryLoading,
        fetchCategories,
        fetchPosts,
        createPost,
        updatePost
    } = useBlog();

    const [editingPost, setEditingPost] = useState(null);

    // Initial data fetch
    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handlePostSelect = (id) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('postId', id);
        setSearchParams(nextParams);
    };

    const handleCloseModal = () => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('postId');
        setSearchParams(nextParams, { replace: true });
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    const handlePostSubmit = async (formData, postId = null) => {
        try {
            if (postId) {
                await updatePost(postId, formData);
                setEditingPost(null);
            } else {
                await createPost(formData);
                setShowCreateModal(false);
            }
            fetchPosts(activeCategoryId, debouncedSearch);
        } catch (error) {
            console.error('Error processing post:', error);
            throw error;
        }
    };

    return (
        <div className="min-h-screen max-w-full">
            <div className="w-full mx-auto px-4 md:px-8 lg:px-12 py-8">
                <BlogHeader
                    activeCategoryId={activeCategoryId}
                    categories={categories}
                    handleChangeCategory={setActiveCategoryId}
                    categoryLoader={categoryLoading}
                    onSearch={setSearchQuery}
                    searchQuery={searchQuery}
                />

                <div className="mt-8">
                    {loading && posts.length === 0 ? (
                        <BlogSkeleton />
                    ) : (
                        <BlogGrid
                            onPostSelect={handlePostSelect}
                            onAddPost={() => setShowCreateModal(true)}
                            activeCategoryId={activeCategoryId}
                            searchQuery={debouncedSearch}
                        />
                    )}
                </div>
            </div>

            {selectedPostId && (
                <BlogPostModal
                    postId={selectedPostId}
                    onClose={handleCloseModal}
                    onEdit={(post) => {
                        handleCloseModal();
                        setEditingPost(post);
                    }}
                />
            )}

            {(showCreateModal || editingPost) && (
                <CreatePostModal
                    initialData={editingPost}
                    onClose={() => {
                        setShowCreateModal(false);
                        setEditingPost(null);
                    }}
                    onSubmit={handlePostSubmit}
                    categories={categories}
                />
            )}
        </div>
    );
}

export default React.memo(BlogPage);